// src/pages/GroupedApplicationsDepartmentHead.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from '../api/axios';
import './GroupedApplicationsDepartmentHead.css';
import ApplicationViewDepartmentHead from './ApplicationViewDepartmentHead';
import AssignStaffPopover from '../components/assign/AssignStaffPopover';
import ResponsibleAvatars from '../components/responsibles/ResponsibleAvatars';
import InExpertiseApplicationsList from './InExpertiseApplicationsList';
import UserProfileModal from '../components/users/UserProfileModal';

export default function GroupedApplicationsDepartmentHead({ locale = 'kz' }) {
  /* ===================== inner tab ===================== */
  const [innerTab, setInnerTab] = useState('new'); // 'new' | 'inExpertise'

  /* ===================== state ===================== */
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedApplicationId, setSelectedApplicationId] = useState(null);
  const [heads, setHeads] = useState([]);
  const [assigningGroupId, setAssigningGroupId] = useState(null);
  const [selectingGroupId, setSelectingGroupId] = useState(null);

  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  const [meta, setMeta] = useState({ total: 0, per_page: 10, current_page: 1, last_page: 1, from: 0, to: 0 });
  const [page, setPage] = useState(1);

  const [expanded, setExpanded] = useState(() => {
    try { return JSON.parse(localStorage.getItem('gadh_expanded_groups') || '{}'); } catch { return {}; }
  });
  const anyOpen = useMemo(() => groups.some((g) => expanded[g.id]), [groups, expanded]);

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignGroup, setAssignGroup] = useState(null);
  const [assignAnchorEl, setAssignAnchorEl] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const [profileOpen, setProfileOpen] = useState(false);
const [profileUser, setProfileUser] = useState(null);
const [profileDetails, setProfileDetails] = useState(null);
const [profileLoading, setProfileLoading] = useState(false);

  const [, forceMinuteTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => forceMinuteTick((x) => x + 1), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  /* ===================== i18n ===================== */
  const t = {
    kz: {
      tabNew: 'Жаңа өтінімдер',
      tabInExpertise: 'Сараптамада',
      group: 'Топ атауы',
      view: 'Толық көру',
      empty: 'Топтар табылмады',
      accept: 'Қабылдау',
      assign: 'Ішкі сарапшыны тағайындау',
      selectHead: 'Ішкі сарапшыны таңдаңыз',
      successAssign: 'Ішкі сарапшы сәтті тағайындалды!',
      successApprove: 'Барлық өтінімдер сәтті қабылданды!',
      loading: 'Жүктелуде...',
      confirmApprove: 'Сіз барлық өтінімдерді қабылдауды растайсыз ба?',
      confirmAssign: 'Сіз осы сарапшыны тағайындауға сенімдісіз бе?',
      error: 'Қате орын алды',
      expandAll: 'Барлығын ашу',
      collapseAll: 'Барлығын жабу',
      apps: 'Өтінімдер',
      overdue: 'Мерзімі өтті',
      start: 'Басталуы',
      deadline: 'Дедлайн',
      assignees: 'Тағайындалғандар',
      assignBtn: 'Тағайындау',
      modalTitle: 'Қызметкерлерді тағайындау',
      cancel: 'Болдырмау',
      save: 'Сақтау',
      staffEmpty: 'Қызметкерлер табылмады',
      pagination: { prev: 'Алдыңғы', next: 'Келесі' },
      tableHeaders: {
        title: 'Оқу басылымы',
        grade: 'Сынып',
        language: 'Әзірлеу тілі',
        category: 'Санат',
        publisher: 'Баспа (шифр)',
        status: 'Күйі',
        date: 'Күні',
        action: '',
      },
    },
    ru: {
      tabNew: 'Новые заявки',
      tabInExpertise: 'На экспертизе',
      group: 'Название группы',
      view: 'Просмотр',
      empty: 'Группы не найдены',
      accept: 'Принять',
      assign: 'Назначить внутреннего эксперта',
      selectHead: 'Выберите внутреннего эксперта',
      successAssign: 'Внутренний эксперт успешно назначен!',
      successApprove: 'Все заявки успешно приняты!',
      loading: 'Загрузка...',
      confirmApprove: 'Вы подтверждаете принятие всех заявок?',
      confirmAssign: 'Вы уверены, что хотите назначить этого эксперта?',
      error: 'Произошла ошибка',
      expandAll: 'Развернуть все',
      collapseAll: 'Свернуть все',
      apps: 'Заявок',
      overdue: 'Просрочено',
      start: 'Начало',
      deadline: 'Дедлайн',
      assignees: 'Назначено',
      assignBtn: 'Назначить',
      modalTitle: 'Назначить сотрудников',
      cancel: 'Отмена',
      save: 'Сохранить',
      staffEmpty: 'Сотрудники не найдены',
      pagination: { prev: 'Назад', next: 'Вперёд' },
      tableHeaders: {
        title: 'Учебное издание',
        grade: 'Класс',
        language: 'Язык разработки',
        category: 'Категория',
        publisher: 'Издательство (шифр)',
        status: 'Статус',
        date: 'Дата',
        action: '',
      },
    },
  }[locale];

  const langHeader = locale === 'ru' ? 'ru' : 'kz';
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Accept-Language': langHeader }),
    [langHeader]
  );

  const staffParams = useMemo(() => ({ per_page: 200, status: 'белсенді', roles: 'internal_expert' }), []);

  /* ===================== responsible helpers ===================== */
  const getResponsibles = (g) =>
    (Array.isArray(g?.responsibles) && g.responsibles) ||
    (Array.isArray(g?.assignees) && g.assignees) || [];

  const respId = (u) => u?.user_id ?? u?.id ?? null;

  const openAssign = useCallback((g, anchorEl) => {
    setAssignGroup(g);
    setSelectedIds(getResponsibles(g).map((u) => respId(u)).filter(Boolean));
    setAssignAnchorEl(anchorEl || null);
    setAssignOpen(true);
  }, []);

  const closeAssign = useCallback(() => {
    setAssignOpen(false);
    setAssignGroup(null);
    setAssignAnchorEl(null);
    setSelectedIds([]);
    setSaving(false);
  }, []);

  /* ===================== helpers ===================== */
  const getStatusName = useCallback(
    (st) => (typeof st === 'object' ? (locale === 'kz' ? st.name_kz : st.name_ru) : (st || '')),
    [locale]
  );

  const safeDate = (s) => (s ? new Date(String(s).replace(' ', 'T')) : null);

  const timeUnits = useMemo(() => (
    locale === 'ru'
      ? { d: 'д', h: 'ч', m: 'м' }
      : { d: 'күн', h: 'сағ', m: 'мин' }
  ), [locale]);

  const formatTimeLeft = useCallback((deadlineAt) => {
    if (!deadlineAt) return '—';
    const deadline = safeDate(deadlineAt);
    if (!deadline) return '—';
    const diffMs = deadline - new Date();
    if (diffMs <= 0) return t.overdue;
    const totalMin = Math.floor(diffMs / 60000);
    const d = Math.floor(totalMin / (60 * 24));
    const h = Math.floor((totalMin % (60 * 24)) / 60);
    const m = totalMin % 60;
    if (d > 0) return `${d}${timeUnits.d} ${h}${timeUnits.h} ${m}${timeUnits.m}`;
    if (h > 0) return `${h}${timeUnits.h} ${m}${timeUnits.m}`;
    return `${m}${timeUnits.m}`;
  }, [t.overdue, timeUnits]);

  const minutesUntil = useCallback((deadlineAt) => {
    if (!deadlineAt) return null;
    const d = safeDate(deadlineAt);
    if (!d) return null;
    return Math.floor((d - new Date()) / 60000);
  }, []);

  const getTimeSeverity = useCallback((deadlineAt) => {
    const mins = minutesUntil(deadlineAt);
    if (mins === null) return 'none';
    if (mins <= 0) return 'danger';
    if (mins <= 60) return 'danger';
    if (mins <= 24 * 60) return 'warn';
    return 'ok';
  }, [minutesUntil]);

  const summarizeGroup = useCallback((group) => {
    const apps = Array.isArray(group.applications) ? group.applications : [];
    let nearest = null;
    let anyOverdue = false;
    apps.forEach((a) => {
      const dl = a?.deadline;
      if (!dl || !dl.deadline_at) return;
      const ts = safeDate(dl.deadline_at)?.getTime() ?? Infinity;
      const nts = safeDate(nearest?.deadline_at)?.getTime() ?? Infinity;
      if (!nearest || ts < nts) nearest = dl;
      if (dl.is_overdue === true) anyOverdue = true;
    });
    if (!nearest) return null;
    const nowOverdue = (safeDate(nearest.deadline_at) ?? new Date(0)) < new Date();
    return {
      start_at: nearest.start_at ?? null,
      deadline_at: nearest.deadline_at ?? null,
      is_overdue: anyOverdue || nowOverdue,
      minutes_left: nearest.minutes_left ?? null,
      progress_percent: typeof nearest.progress_percent === 'number' ? nearest.progress_percent : null,
    };
  }, []);

  const groupSummaries = useMemo(() => {
    const map = {};
    groups.forEach((g) => { map[g.id] = summarizeGroup(g); });
    return map;
  }, [groups, summarizeGroup]);

  /* ===================== fetch ===================== */
  const fetchGroups = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/expertise/grouped', { headers: authHeaders });
      const payload = res.data;
      const items = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
      setGroups(items);
      if (payload?.meta) {
        setMeta(payload.meta);
      } else {
        const n = items.length;
        setMeta({ total: n, per_page: n, current_page: 1, last_page: 1, from: n ? 1 : 0, to: n });
      }
    } catch (err) {
      setError(err?.response?.data?.message || t.error);
      setGroups([]);
      setMeta({ total: 0, per_page: 10, current_page: 1, last_page: 1, from: 0, to: 0 });
    } finally {
      setLoading(false);
    }
  }, [authHeaders, t.error]);

  const fetchAvailableHeads = useCallback(async () => {
    try {
      const res = await axios.get(`/expertise/groups/available-heads`, { headers: authHeaders });
      setHeads(res.data?.heads || []);
    } catch (err) {

    }
  }, [authHeaders]);

  const saveAssign = useCallback(async () => {
    if (!assignGroup || !selectedIds[0]) return;
    setSaving(true);
    try {
      await axios.post(
        `/expertise/department-head/groups/${assignGroup.id}/assign-internal-expert`,
        { user_id: selectedIds[0] },
        { headers: authHeaders }
      );
      closeAssign();
      fetchGroups();
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || t.error);
      setSaving(false);
    }
  }, [assignGroup, selectedIds, authHeaders, closeAssign, fetchGroups, t.error]);

  const removeResponsible = useCallback(async (group, userId) => {
    if (!group?.id || !userId) return;
    try {
      await axios.post(
        `/backoffice/groups/${group.id}/responsibles-remove`,
        { user_id: userId },
        { headers: authHeaders }
      );
      fetchGroups();
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || t.error);
    }
  }, [authHeaders, fetchGroups, t.error]);

  const openProfile = useCallback(async (user) => {
  if (!user) return;
  setProfileUser(user);
  setProfileOpen(true);
  setProfileDetails(null);
  setProfileLoading(true);
  const uId = user?.user_id ?? user?.id;
  if (!uId) { setProfileLoading(false); return; }
  try {
    const res = await axios.get(`/backoffice/users/${uId}`, { headers: authHeaders });
    setProfileDetails(res.data?.data || res.data || null);
  } catch {
    setProfileDetails(null);
  } finally {
    setProfileLoading(false);
  }
}, [authHeaders]);

const closeProfile = useCallback(() => {
  setProfileOpen(false);
  setProfileUser(null);
  setProfileDetails(null);
  setProfileLoading(false);
}, []);

  /* ===================== effects ===================== */
  useEffect(() => { fetchAvailableHeads(); }, [fetchAvailableHeads]);
  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  useEffect(() => {
    setExpanded((prev) => {
      const next = {};
      groups.forEach((g) => { next[g.id] = prev[g.id] ?? false; });
      localStorage.setItem('gadh_expanded_groups', JSON.stringify(next));
      return next;
    });
  }, [groups]);

  /* ===================== ui helpers ===================== */
  const onHeaderSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(key); setSortDir('asc'); }
  };

  const displayStatus = useCallback((st) => {
    const raw = typeof st === 'object' ? (locale === 'kz' ? st.name_kz : st.name_ru) : (st || '');
    return raw;
  }, [locale]);

  const toggleGroup = useCallback((id) => {
    setExpanded((prev) => {
      const next = { ...prev, [id]: !(prev[id] ?? false) };
      localStorage.setItem('gadh_expanded_groups', JSON.stringify(next));
      return next;
    });
  }, []);

  const toggleAllGroups = useCallback(() => {
    setExpanded((prev) => {
      const next = { ...prev };
      const target = !anyOpen;
      groups.forEach((g) => (next[g.id] = target));
      localStorage.setItem('gadh_expanded_groups', JSON.stringify(next));
      return next;
    });
  }, [anyOpen, groups]);

  const openGroup = useCallback((id) => {
    setExpanded((prev) => {
      const next = { ...prev, [id]: true };
      localStorage.setItem('gadh_expanded_groups', JSON.stringify(next));
      return next;
    });
  }, []);

  const handleSelectHead = useCallback(async (groupId, headId) => {
    if (!window.confirm(t.confirmAssign)) return;
    setAssigningGroupId(groupId);
    try {
      await axios.post(
        `/expertise/department-head/groups/${groupId}/assign-internal-expert`,
        { user_id: headId },
        { headers: authHeaders }
      );
      alert(t.successAssign);
      await fetchGroups();
      setSelectingGroupId(null);
      openGroup(groupId);
    } catch (err) {

      alert(t.error);
    } finally {
      setAssigningGroupId(null);
    }
  }, [authHeaders, fetchGroups, openGroup, t]);

  /* ===================== tab switcher UI ===================== */
  const TabSwitcher = (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        background: '#e2e8f0',
        borderRadius: 8,
        padding: 4,
        marginBottom: 20,
        width: 'fit-content',
      }}
    >
      {[
        { key: 'new',        label: t.tabNew },
        { key: 'inExpertise', label: t.tabInExpertise },
      ].map(({ key, label }) => {
        const isActive = innerTab === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => setInnerTab(key)}
            style={{
              padding: '8px 20px',
              borderRadius: 6,
              border: 'none',
              cursor: 'pointer',
              fontWeight: isActive ? 600 : 400,
              fontSize: 13,
              background: isActive ? '#fff' : 'transparent',
              color: isActive ? '#0f172a' : '#64748b',
              boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  /* ===================== render ===================== */
  return (
    <div className="gad2">
      {/* Переключатель табов */}
      {TabSwitcher}

      {/* ── Таб: Сараптамада ── */}
      {innerTab === 'inExpertise' && (
        <InExpertiseApplicationsList locale={locale} />
      )}

      {/* ── Таб: Жаңа өтінімдер ── */}
      {innerTab === 'new' && (
        <>
          {/* Toolbar */}
          <div className="gad2-toolbar">
            <div className="gad2-left">
              <button className="gad2-btn" onClick={toggleAllGroups}>
                {anyOpen ? t.collapseAll : t.expandAll}
              </button>
            </div>
          </div>

          {loading && groups.length === 0 && <p>{t.loading}</p>}
          {!loading && !groups.length && !error && <p>{t.empty}</p>}
          {error && <div className="gad2-error">{error}</div>}

          {/* List */}
          {groups.map((group) => {
            const apps = Array.isArray(group.applications) ? group.applications : [];
            const responsibles = getResponsibles(group);

            const allReadyForAssign = apps.length > 0 && apps.every((app) => {
              const s = getStatusName(app.status);
              return s === 'Бөлім басшысы қабылдады' || s === 'department_supervisor принял';
            });

            const alreadyAssigned = apps.length > 0 && apps.every((app) => !!app.assigned_head_id);
            const isOpen = expanded[group.id] ?? false;

            const summary = groupSummaries[group.id];
            const timeText = summary ? formatTimeLeft(summary.deadline_at) : '—';
            const severity = getTimeSeverity(summary?.deadline_at);

            return (
              <div key={group.id} className={`group-section ${isOpen ? 'is-open' : 'is-closed'}`}>
                {/* HEADER */}
                <div
                  className="gad2-group-header"
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleGroup(group.id)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleGroup(group.id)}
                  aria-expanded={isOpen}
                  aria-controls={`gadh-panel-${group.id}`}
                >
                  <span className="gad2-chev">{isOpen ? '▾' : '▸'}</span>

                  <span className="gad2-group-title">
                    {group.title || '—'}
                    {group.publisher_name && (
                      <span className="gad2-group-publisher"> — {group.publisher_name}</span>
                    )}
                  </span>

                  {group.sent_at && (
                    <span className="gad2-group-count" title="Жіберілген күні">
                      {group.sent_at}
                    </span>
                  )}

                  <span className="gad2-group-count">{t.apps}: {apps.length}</span>

                  <span
                    className={`gad2-deadline ${severity} ${summary?.is_overdue ? 'overdue' : ''}`}
                    title={summary
                      ? `${t.start}: ${summary.start_at || '—'}\n${t.deadline}: ${summary.deadline_at || '—'}`
                      : ''}
                  >
                    {timeText}
                  </span>

                  {allReadyForAssign && !alreadyAssigned && (
                    <button
                      className="btn-assign-head"
                      onClick={(e) => {
                        e.stopPropagation();
                        openGroup(group.id);
                        setSelectingGroupId(group.id);
                      }}
                      disabled={assigningGroupId === group.id}
                    >
                      {assigningGroupId === group.id ? t.loading : t.assign}
                    </button>
                  )}

                  <span
                    onClick={(e) => e.stopPropagation()}
                    style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}
                  >
                    <ResponsibleAvatars
  items={responsibles}
  canManage={true}
  onViewProfile={openProfile}
  max={4}
  showAdd={true}
  addTitle={t.assignBtn}
  onAdd={(e) => {
    e.stopPropagation();
    openAssign(group, e.currentTarget);
  }}
  onRemove={(uid) => removeResponsible(group, uid)}
  label={t.assignees}
/>
                  </span>
                </div>

                {/* Progress bar */}
                {isOpen && summary?.progress_percent != null && (
                  <div
                    className="gad2-progress"
                    role="progressbar"
                    aria-valuenow={Math.round(summary.progress_percent * 100)}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  >
                    <div
                      className="gad2-progress-bar"
                      style={{ width: `${Math.round(summary.progress_percent * 100)}%` }}
                    />
                  </div>
                )}

                {/* Panel */}
                {isOpen && (
                  <div id={`gadh-panel-${group.id}`} className="gad2-group-panel">
                    <table className="custom-table" aria-label="applications-table">
                      <thead>
                        <tr>
                          <ThSort label={t.tableHeaders.title}     sortKey="title"          sortBy={sortBy} sortDir={sortDir} onSort={onHeaderSort} />
                          <Th label={t.tableHeaders.grade} />
                          <Th label={t.tableHeaders.language} />
                          <Th label={t.tableHeaders.category} />
                          <ThSort label={t.tableHeaders.publisher} sortKey="publisher_name" sortBy={sortBy} sortDir={sortDir} onSort={onHeaderSort} />
                          <Th label={t.tableHeaders.status} />
                          <ThSort label={t.tableHeaders.date}      sortKey="created_at"     sortBy={sortBy} sortDir={sortDir} onSort={onHeaderSort} />
                          <Th label={t.tableHeaders.action} />
                        </tr>
                      </thead>
                      <tbody>
                        {apps.map((app) => {
                          const dl = app?.deadline;
                          return (
                            <tr key={app.id}>
                              <td>{group.title || '—'}</td>
                              <td>{group.grade || '—'}</td>
                              <td>{group.development_language || '—'}</td>
                              <td>{app.category || '—'}</td>
                              <td>{group.encrypted_publisher_name || group.publisher_name || '—'}</td>
                              <td>{displayStatus(app.status)}</td>
                              <td title={dl?.deadline_at ? `${t.deadline}: ${dl.deadline_at}` : undefined}>
                                {app.created_at || '—'}
                              </td>
                              <td>
                                <button className="btn-view" onClick={() => setSelectedApplicationId(app.id)}>
                                  {t.view}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {selectingGroupId === group.id && (
                      <div className="select-head-list">
                        <h4>{t.selectHead}:</h4>
                        {heads.length === 0 ? (
                          <p className="muted">—</p>
                        ) : (
                          <ul>
                            {heads.map((head) => (
                              <li key={head.id}>
                                <button
                                  className="btn-select-head"
                                  title={head.name}
                                  onClick={() => handleSelectHead(group.id, head.id)}
                                >
                                  {head.name}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Pagination */}
          <Pagination
            meta={meta}
            page={meta.current_page || page}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(meta.last_page || 1, p + 1))}
            onGoto={(p) => setPage(p)}
          />
        </>
      )}

      

      {/* Application modal */}
      {selectedApplicationId && (
        <ApplicationViewDepartmentHead
          applicationId={selectedApplicationId}
          onClose={() => { setSelectedApplicationId(null); fetchGroups(); }}
          onChanged={fetchGroups}
          locale={locale}
        />
      )}

      {/* Assign staff popover */}
      <AssignStaffPopover
        open={assignOpen}
        anchorEl={assignAnchorEl}
        title={t.modalTitle}
        subtitle={assignGroup?.title || (assignGroup ? `#${assignGroup.id}` : '')}
        headers={authHeaders}
        staffEndpoint="/backoffice/users"
        staffParams={staffParams}
        value={selectedIds}
        onChange={setSelectedIds}
        onClose={closeAssign}
        onSave={saveAssign}
        saving={saving}
        loadingText={t.loading}
        emptyText={t.staffEmpty}
        cancelText={t.cancel}
        saveText={t.save}
      />

      {profileOpen && (
  <div
    className="ga-modal-backdrop"
    role="dialog"
    aria-modal="true"
    onClick={closeProfile}
  >
    <div
      className="ga-modal-card ga-modal-card--xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="ga-modal-head">
        <h3 className="ga-modal-title">
          {locale === 'kz' ? 'Қолданушы профилі' : 'Профиль пользователя'}
        </h3>
        <button type="button" className="ga-modal-close" onClick={closeProfile}>×</button>
      </div>
      <div className="ga-modal-body">
        {profileLoading ? (
          <p>{t.loading}</p>
        ) : (
          <UserProfileModal
            user={profileUser}
            userDetails={profileDetails}
            locale={locale}
            canWriteReview={false}
            canManageReview={false}
          />
        )}
      </div>
    </div>
  </div>
)}
    </div>
  );
}

/* ===================== small presentational helpers ===================== */
function Th({ label }) { return <th>{label}</th>; }

function ThSort({ label, sortKey, sortBy, sortDir, onSort }) {
  const active = sortBy === sortKey;
  return (
    <th
      className={`th-sort ${active ? 'is-active' : ''}`}
      onClick={() => onSort(sortKey)}
      role="button"
      tabIndex={0}
    >
      <span>{label}</span>
      <span className="sort-caret">{active ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</span>
    </th>
  );
}

function Pagination({ meta, page, onPrev, onNext, onGoto }) {
  const total = meta?.total ?? 0;
  const last = meta?.last_page ?? 1;
  const from = meta?.from ?? 0;
  const to = meta?.to ?? 0;

  const win = 2;
  const start = Math.max(1, page - win);
  const end = Math.min(last, page + win);
  const pages = [];
  for (let p = start; p <= end; p++) pages.push(p);

  return (
    <div className="gad2-pager">
      <div className="gad2-pager-info">{total > 0 ? `${from}–${to} / ${total}` : ''}</div>
      <div className="gad2-pager-buttons">
        <button className="gad2-btn" onClick={onPrev} disabled={page <= 1}>‹</button>
        {start > 1 && (
          <>
            <button className={`gad2-btn ${page === 1 ? 'is-current' : ''}`} onClick={() => onGoto(1)}>1</button>
            {start > 2 && <span className="dots">…</span>}
          </>
        )}
        {pages.map((p) => (
          <button key={p} className={`gad2-btn ${page === p ? 'is-current' : ''}`} onClick={() => onGoto(p)}>{p}</button>
        ))}
        {end < last && (
          <>
            {end < last - 1 && <span className="dots">…</span>}
            <button className={`gad2-btn ${page === last ? 'is-current' : ''}`} onClick={() => onGoto(last)}>{last}</button>
          </>
        )}
        <button className="gad2-btn" onClick={onNext} disabled={page >= last}>›</button>
      </div>
    </div>
  );
}